/* ==========================================================================
   Git Commit Templates Library — Vanilla JS
   Template editor, workflow selection, copy/download, and analytics.
   All essential content is server-rendered; this script only enhances it.
   ========================================================================== */

(function () {
  'use strict';

  var TABS = ['tower', 'git', 'agents'];
  var PANEL_IDS = { tower: 'use-in-tower', git: 'use-with-git', agents: 'use-with-agents' };

  /* ── Analytics ─────────────────────────────────────────────────────
     Records template identifiers and actions only — never commit text. */

  var currentSlug = null;

  function track(action, detail) {
    if (typeof FN === 'undefined' || !FN.analytics || !FN.analytics.sendEventToGA) return;
    var label = 'commit-templates' + (currentSlug ? '/' + currentSlug : '') + (detail ? '/' + detail : '');
    FN.analytics.sendEventToGA({ eventCategory: 'commit-templates', eventAction: action, eventLabelSuffix: label });
  }

  /* ── Clipboard ─────────────────────────────────────────────────── */

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      document.body.removeChild(ta);
      ok ? resolve() : reject(new Error('copy failed'));
    });
  }

  var shortcut = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘C' : 'Ctrl+C';

  function flashButton(btn, text) {
    var original = btn.getAttribute('data-label') || btn.textContent;
    btn.setAttribute('data-label', original);
    btn.textContent = text;
    btn.classList.add('copied');
    clearTimeout(btn._ctTimer);
    btn._ctTimer = setTimeout(function () {
      btn.textContent = original;
      btn.classList.remove('copied');
    }, 2000);
  }

  function setStatus(el, message) {
    if (!el) return;
    el.textContent = message;
    clearTimeout(el._ctTimer);
    el._ctTimer = setTimeout(function () { el.textContent = ''; }, 4000);
  }

  // Selects an element's text so the visitor can copy it manually
  function selectContents(el) {
    if (el.select) { el.focus(); el.select(); return; }
    var range = document.createRange();
    range.selectNodeContents(el);
    var selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  }

  /* ── Detail page ───────────────────────────────────────────────── */

  function initTabs(root) {
    var tablist = root.querySelector('[data-ct-tablist]');
    if (!tablist) return;

    var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[data-ct-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-ct-panel]'));

    tablist.hidden = false;
    tablist.setAttribute('role', 'tablist');
    tabs.forEach(function (tab) { tab.setAttribute('role', 'tab'); });
    panels.forEach(function (panel) {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('tabindex', '0');
    });

    function activate(name, options) {
      options = options || {};
      tabs.forEach(function (tab) {
        var selected = tab.getAttribute('data-ct-tab') === name;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
        if (selected && options.focusTab) tab.focus();
      });
      panels.forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-ct-panel') !== name;
      });
      if (options.updateHash && window.history.replaceState) {
        window.history.replaceState(null, '', '#' + PANEL_IDS[name]);
      }
      if (options.track) track('usage-tab', name);
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function (event) {
        event.preventDefault();
        var name = tab.getAttribute('data-ct-tab');
        activate(name, { focusTab: true, updateHash: true, track: true });
      });
      tab.addEventListener('keydown', function (event) {
        if (event.key === ' ') {
          event.preventDefault();
          tab.click();
          return;
        }
        var next = null;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        activate(tabs[next].getAttribute('data-ct-tab'), { focusTab: true, updateHash: true, track: true });
      });
    });

    // Start on the tab named in the URL hash (shareable), else Tower
    function activateFromHash() {
      var initial = 'tower';
      TABS.forEach(function (name) {
        if (window.location.hash === '#' + PANEL_IDS[name]) initial = name;
      });
      activate(initial);
    }
    activateFromHash();
    window.addEventListener('hashchange', activateFromHash);
  }

  function initPlatformSwitch(root) {
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-ct-set-platform]'));

    function setPlatform(platform) {
      root.setAttribute('data-platform', platform);
      buttons.forEach(function (button) {
        button.setAttribute('aria-pressed', button.getAttribute('data-ct-set-platform') === platform ? 'true' : 'false');
      });
    }

    root.querySelectorAll('[data-ct-switch]').forEach(function (group) { group.hidden = false; });
    buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        setPlatform(button.getAttribute('data-ct-set-platform'));
      });
    });

    // The server preselects the visitor's platform; honor a later OS switch
    setPlatform(root.getAttribute('data-platform') || 'mac');
  }

  function initEditor(root) {
    var editor = root.querySelector('#ct-editor');
    var status = root.querySelector('[data-ct-editor-status]');
    var copyButton = root.querySelector('[data-ct-copy-editor]');
    var resetButton = root.querySelector('[data-ct-reset-editor]');
    if (!editor) return;

    copyButton.hidden = false;
    resetButton.hidden = false;

    function updateResetState() {
      resetButton.disabled = editor.value === editor.defaultValue;
    }

    copyButton.addEventListener('click', function () {
      copyText(editor.value).then(function () {
        flashButton(copyButton, 'Copied!');
        setStatus(status, 'Template copied to the clipboard.');
        track('copy-template');
      }, function () {
        selectContents(editor);
        setStatus(status, 'Couldn\'t copy automatically. The text is selected; press ' + shortcut + ' to copy it.');
      });
    });

    resetButton.addEventListener('click', function () {
      editor.value = editor.defaultValue;
      updateResetState();
      setStatus(status, 'Template reset to the library version.');
      editor.focus();
    });

    editor.addEventListener('input', updateResetState);
    updateResetState();

    // Downloads use the current editor contents. Without Blob support the
    // link falls back to the server's copy of the library version.
    root.querySelectorAll('[data-ct-download-editor]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        track('download-template');
        if (typeof Blob === 'undefined' || !window.URL || !URL.createObjectURL) return;
        event.preventDefault();
        var text = editor.value.replace(/\r\n/g, '\n');
        if (text.slice(-1) !== '\n') text += '\n';
        var url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
        var temp = document.createElement('a');
        temp.href = url;
        temp.download = link.getAttribute('data-filename');
        document.body.appendChild(temp);
        temp.click();
        document.body.removeChild(temp);
        setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
        setStatus(status, 'Downloading ' + link.getAttribute('data-filename') + '.');
      });
    });
  }

  function initCopyBlocks(root) {
    root.querySelectorAll('[data-ct-copy-block]').forEach(function (button) {
      var block = button.parentNode.querySelector('pre');
      button.hidden = false;
      button.addEventListener('click', function () {
        copyText(block.textContent).then(function () {
          flashButton(button, 'Copied!');
          track(button.getAttribute('data-ct-event') || 'copy-command');
        }, function () {
          selectContents(block);
          flashButton(button, 'Press ' + shortcut);
        });
      });
    });
  }

  function initDetail(root) {
    currentSlug = root.getAttribute('data-slug');
    root.closest('.commit-templates').classList.add('is-enhanced');

    initTabs(root);
    initPlatformSwitch(root);
    initEditor(root);
    initCopyBlocks(root);

    root.querySelectorAll('[data-ct-event]').forEach(function (link) {
      if (link.tagName === 'A') {
        link.addEventListener('click', function () { track(link.getAttribute('data-ct-event')); });
      }
    });

    track('view');
  }

  document.addEventListener('DOMContentLoaded', function () {
    var detail = document.querySelector('[data-ct-detail]');
    if (detail) initDetail(detail);
  });

})();
